import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@common/components';
import { FormError } from '@common/components/form/FormError';
import { FormField } from '@common/components/form/FormField';
import { Label } from '@common/components/form/Label';
import { Textarea } from '@common/components/form/Textarea';
import { Stack } from '@common/components/Stack';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from '@lang';

import { useCancelOrder } from '../mutations';

const CancelOrderFormSchema = z.object({
  cancelMessage: z.string().min(1),
});

type CancelOrderFormValues = z.infer<typeof CancelOrderFormSchema>;

type CancelOrderFormProps = {
  onOrderCancelled?: () => void;
  guestEmail: string;
  guestUuid: string;
  siteSlug: string;
  orderUuid: string;
};

export function CancelOrderForm({
  onOrderCancelled,
  guestEmail,
  guestUuid,
  siteSlug,
  orderUuid,
  ...rest
}: CancelOrderFormProps) {
  const { t } = useTranslations();

  const {
    mutate: cancelOrder,
    isLoading,
    isError,
    error,
  } = useCancelOrder({
    onSuccess: onOrderCancelled,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<CancelOrderFormValues>({
    resolver: zodResolver(CancelOrderFormSchema),
  });

  return (
    <>
      <form
        {...rest}
        onSubmit={handleSubmit((data) => {
          cancelOrder({
            message: data.cancelMessage,
            orderId: orderUuid,
            guestEmail,
            guestUuid,
            siteSlug,
          });
        })}
      >
        <fieldset disabled={isLoading}>
          <Stack>
            <p>{t('cancelOrderForm.title')}</p>

            {isError && <FormError error={error} />}

            <FormField>
              <Label htmlFor="cancelMessage" required>
                {t('cancelOrderForm.amend.label')}
              </Label>

              <Textarea
                {...register('cancelMessage')}
                id="cancelMessage"
                size="xl"
                rows={5}
                placeholder={t('cancelOrderForm.amend.placeholder')}
                hasError={!!errors.cancelMessage}
              />
            </FormField>

            <FormField>
              <Button isPrimary type="submit" disabled={!isValid || isLoading}>
                {t('cancelOrderForm.submit.label')}
              </Button>
            </FormField>
          </Stack>
        </fieldset>
      </form>
    </>
  );
}
